/* eslint-disable react-hooks/exhaustive-deps */
import { useContext, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '../../../../components/shared/MaterialUI';
import { TablePagination, Typography } from '@mui/material';

import { useUI } from '../../../../app/context/ui';
import AppHelper from '../../../../helpers/AppHelper';
import PayrollService from '../../../../services/newApi/PayrollService';
import CompanyRow from './CompanyRow';
import EmployeeSubTable from './EmployeeSubTable';
import { payrollContext } from '../../payrollView';
import { useSelector } from 'react-redux';
import { TablePaginationActions } from '../../../../helpers/TablePaginationAction';

const HierarchicalPayrollTable = ({ employeeHandlers }) => {
  const [parentCompanies, setParentCompanies] = useState([]);
  const [filteredEmployees, setFilteredEmployees] = useState(null);
  const [pages, setPages] = useState({ page: 1, size: 10, totalRows: 0 });
  const { blockUI, snackbarUI } = useUI();
  const payrollService = useMemo(() => new PayrollService(), []);
  const { currentTab, filterModal } = useContext(payrollContext);
  const state = useSelector((state) => state);
  const organizationId = state?.organizationReducer?.organizationSelected?.id;

  const hasEmployeeFilter = !!filterModal?.EmployeeName;

  useEffect(() => {
    if (!organizationId) return;
    if (hasEmployeeFilter) {
      fetchEmployeesByName();
    } else {
      setFilteredEmployees(null);
      fetchParentCompanies(1, pages.size);
    }
  }, [organizationId, currentTab, filterModal]);

  const fetchParentCompanies = async (pageNumber, pageSize) => {
    try {
      blockUI.current.open(true);
      const params = {
        ActiveEmployees: true,
        PageNumber: pageNumber || pages.page,
        PageSize: pageSize || pages.size,
      };
      if (filterModal?.Employeer) params.Name = filterModal.Employeer;
      const response = await payrollService.getCompanies(organizationId, params);
      setParentCompanies(response?.data?.data || []);
      const pagination = response?.data?.pagination;
      if (pagination) {
        setPages({
          page: pagination.pageNumber ?? 1,
          size: pagination.pageSize ?? 10,
          totalRows: pagination.totalRecords ?? 0,
        });
      }
      blockUI.current.open(false);
    } catch (error) {
      blockUI.current.open(false);
      AppHelper.checkError(error, snackbarUI);
    }
  };

  const fetchEmployeesByName = async () => {
    const isMaxedOut = currentTab === 2;
    try {
      blockUI.current.open(true);
      const filters = {};
      if (filterModal?.Employeer) filters.Employeer = filterModal.Employeer;
      if (filterModal?.EmployeeName) filters.EmployeeName = filterModal.EmployeeName;
      const response = await payrollService.getEmployeesActiveTab(
        organizationId,
        { page: 1, size: 200 },
        filters,
        isMaxedOut
      );
      const data = response?.data?.data?.map((item) => ({
        ...item,
        id: item?.employeeId,
      })) || [];
      setFilteredEmployees(data);
      blockUI.current.open(false);
    } catch (error) {
      blockUI.current.open(false);
      AppHelper.checkError(error, snackbarUI);
    }
  };

  const handleChangePage = (event, newPage) => {
    const page = newPage === 0 ? 1 : newPage === pages.page ? newPage + 1 : newPage;
    setPages((prev) => ({ ...prev, page: parseInt(page) }));
    fetchParentCompanies(page, pages.size);
  };

  const handleChangeRowsPerPage = (event) => {
    const rows = parseInt(event.target.value, 10);
    setPages((prev) => ({ ...prev, page: 1, size: rows }));
    fetchParentCompanies(1, rows);
  };

  // When employee filter is active, group results by employer
  if (filteredEmployees !== null) {
    const grouped = {};
    filteredEmployees.forEach((emp) => {
      const key = emp.employeer || 'Unknown';
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(emp);
    });

    return (
      <Box>
        {Object.keys(grouped).length > 0 ? (
          Object.entries(grouped).map(([employer, employees]) => (
            <Box key={employer} sx={{ mb: 2 }}>
              <Typography sx={{ fontWeight: 600, fontSize: '0.95rem', p: '12px 16px', backgroundColor: '#f5f5f5', borderBottom: '1px solid #e0e0e0' }}>
                {employer} ({employees.length})
              </Typography>
              <EmployeeSubTable
                employees={employees}
                currentTab={currentTab}
                employeeHandlers={employeeHandlers}
                indent={0}
                onRefresh={fetchEmployeesByName}
              />
            </Box>
          ))
        ) : (
          <Box sx={{ padding: '40px 0', textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              No employees found
            </Typography>
          </Box>
        )}
      </Box>
    );
  }

  // Normal hierarchical view
  return (
    <Box>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 60 }} />
              <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem' }}>
                Company name
              </TableCell>
              <TableCell align="center" sx={{ fontWeight: 600, fontSize: '0.875rem', width: 150 }}>
                Active Employees
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {parentCompanies.length > 0 ? (
              parentCompanies.map((company) => (
                <CompanyRow
                  key={`${company.id}-${filterModal?.Employeer || ''}`}
                  company={company}
                  level={0}
                  organizationId={organizationId}
                  currentTab={currentTab}
                  employeeHandlers={employeeHandlers}
                  filterModal={filterModal}
                />
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={3} align="center">
                  <Box sx={{ padding: '40px 0' }}>
                    <Typography variant="body2" color="text.secondary">
                      No companies found
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        rowsPerPageOptions={[10, 20, 50]}
        count={pages.totalRows || 0}
        rowsPerPage={pages.size}
        page={pages.page < 0 || pages.page === 0 ? 0 : pages.page - 1}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        ActionsComponent={() => TablePaginationActions(pages, handleChangePage)}
      />
    </Box>
  );
};

export default HierarchicalPayrollTable;
